/**
 * Series Management — F-12 (Creator+ only)
 * Lists user's series with Create Series button.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { Navbar } from '../components/layout/Navbar';
import { useUserProfile, isCreatorPlus } from '../hooks/useUserProfile';

interface SeriesItem {
  id: string;
  title: string;
  genre?: string;
  bookCount: number;
  wordCount: number;
  characterCount?: number;
}

interface SeriesListResponse {
  series: SeriesItem[];
}

export function SeriesPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newGenre, setNewGenre] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: profile } = useUserProfile();

  // Compute tier flag BEFORE passing to useQuery `enabled` — must be above hooks
  const isCreatorPlusUser = isCreatorPlus(profile);

  // FIX QA-19 HIGH-1: ALL hooks declared before any conditional return.
  // `enabled: isCreatorPlusUser` prevents API call for free/starter users.
  const { data, isLoading } = useQuery({
    queryKey: ['series'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<SeriesListResponse>>('/series');
      return res.data.data ?? null;
    },
    enabled: isCreatorPlusUser,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<ApiSuccess<{ series: SeriesItem }>>('/series', {
        title: newTitle.trim(),
        genre: newGenre.trim() || undefined,
      });
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['series'] });
      setShowCreate(false);
      setNewTitle('');
      setNewGenre('');
      setCreateError(null);
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) => {
      setCreateError(err.response?.data?.error?.message ?? 'Failed to create series. Please try again.');
    },
  });

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (seriesId: string) => {
      await api.delete(`/series/${seriesId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['series'] });
      setConfirmDeleteId(null);
    },
  });

  // Conditional return AFTER all hooks — upgrade prompt for non-Creator+ users
  if (profile !== undefined && !isCreatorPlusUser) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar variant="authed" />
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <div className="text-6xl mb-6">🔗</div>
          <h1 className="text-2xl font-bold text-foreground mb-3">Series Management</h1>
          <p className="text-muted-foreground mb-2">
            Link books into a series and keep characters, settings, and plot threads consistent across sequels.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Series management is available on <strong className="text-foreground">Creator</strong> and <strong className="text-foreground">Publisher</strong> plans.
          </p>
          <Link
            to="/pricing"
            className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold"
          >
            Upgrade to Creator →
          </Link>
          <Link
            to="/dashboard"
            className="block mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const series = data?.series ?? [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="authed" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="mb-6">
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-foreground">Series</h1>
              <p className="text-sm text-muted-foreground">Link books, share story memory, maintain world consistency across sequels.</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowCreate(true)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-semibold"
              >
                + New Series
              </button>
            </div>
          </div>

          {showCreate && (
            <div className="mb-6 p-5 border border-primary/30 rounded-xl bg-card">
              <h2 className="font-semibold text-foreground mb-4">New Series</h2>
              <div className="space-y-3">
                <div>
                  <label htmlFor="series-title" className="block text-sm font-medium text-foreground mb-1">Series Title</label>
                  <input
                    id="series-title"
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="e.g. The Shadowhunter Chronicles"
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label htmlFor="series-genre" className="block text-sm font-medium text-foreground mb-1">Genre (optional)</label>
                  <input
                    id="series-genre"
                    type="text"
                    value={newGenre}
                    onChange={(e) => setNewGenre(e.target.value)}
                    placeholder="e.g. Dark Fantasy"
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                {createError && <p className="text-sm text-destructive">{createError}</p>}
                <div className="flex gap-3">
                  <button
                    onClick={() => createMutation.mutate()}
                    disabled={!newTitle.trim() || createMutation.isPending}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Series'}
                  </button>
                  <button
                    onClick={() => { setShowCreate(false); setNewTitle(''); setNewGenre(''); setCreateError(null); }}
                    className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {!isLoading && series.length === 0 && !showCreate && (
            <div className="text-center py-20">
              <div className="text-6xl mb-5">📚</div>
              <h2 className="text-xl font-bold text-foreground mb-3">Start a series. Build a world.</h2>
              <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
                Link your books into a series and keep characters, settings, and plot threads consistent automatically.
              </p>
              <button
                onClick={() => setShowCreate(true)}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold"
              >
                + Create my first series
              </button>
            </div>
          )}

          {series.map((s) => (
            <div key={s.id} className="mb-4">
              {confirmDeleteId === s.id ? (
                <div className="p-5 border border-destructive/40 rounded-xl bg-card">
                  <p className="text-sm font-medium text-foreground mb-1">Delete "{s.title}"?</p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Your books won't be deleted, but they'll be unlinked from this series. You'll lose the ability to create more stories based on it.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => deleteMutation.mutate(s.id)}
                      disabled={deleteMutation.isPending}
                      className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? 'Deleting…' : 'Yes, delete series'}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setConfirmDeleteId(s.id)}
                    className="absolute top-3 right-3 p-1.5 text-muted-foreground hover:text-destructive transition-colors z-10"
                    aria-label={`Delete ${s.title}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <Link
                    to={`/series/${s.id}`}
                    className="block p-5 border border-border rounded-xl bg-card hover:border-primary/40 transition-colors"
                  >
                    <div className="pr-8">
                      <h3 className="font-bold text-foreground mb-1">{s.title}</h3>
                      <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
                        {s.genre && (
                          <span className="px-2 py-0.5 bg-primary/15 text-primary/80 rounded-full text-xs font-medium">{s.genre}</span>
                        )}
                        <span>{s.bookCount} {s.bookCount === 1 ? 'book' : 'books'}</span>
                        <span>·</span>
                        <span>{(s.wordCount ?? 0).toLocaleString()} words</span>
                      </div>
                      {(s.characterCount ?? 0) > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Story Memory: {s.characterCount} characters
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              )}
            </div>
          ))}
      </main>
    </div>
  );
}
